/**
 * CaptureResult — pagina publica de resultados de la autoevaluacion del cliente.
 *
 * Ruta `/capture/:token/resultado`. Sin sesion ni auth — la autoridad la da
 * el mismo token de captura. ADR-041 D2.
 *
 * Muestra al cliente su nivel de cumplimiento (banda de madurez), score global,
 * mapa de calor por dominio y hallazgos automaticos al terminar su formulario.
 *
 * Comportamiento honesto Fase A:
 *   - Si `informative_only===true`, el score se rotula como PRELIMINAR/INFORMATIVO,
 *     no como veredicto de cumplimiento (las plantillas cliente aun no tienen reglas).
 *   - Si no hay hallazgos (caso normal en Fase A), la seccion se muestra con el
 *     mensaje explicativo — no se oculta para no dar falsa sensacion de "sin problemas".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { type CaptureResultView, fetchCaptureReport, fetchCaptureResult } from '../api/capture.js';
import { DomainHeatMap, scoreColor } from '../components/DomainHeatMap.js';

type ResultErrorKind =
  | 'not_found'
  | 'expired'
  | 'revoked'
  | 'rate_limited'
  | 'not_submitted'
  | 'unknown';

function classifyResultError(err: unknown): ResultErrorKind {
  const msg = err instanceof Error ? err.message : '';
  if (msg.startsWith('400:')) return 'not_found';
  if (msg.startsWith('401:')) return 'not_found';
  if (msg.startsWith('409:')) return 'not_submitted';
  if (msg.startsWith('410:')) {
    if (msg.includes('expired')) return 'expired';
    if (msg.includes('revoked')) return 'revoked';
    return 'expired';
  }
  if (msg.startsWith('429:')) return 'rate_limited';
  return 'unknown';
}

/** Color de fondo del badge de banda segun el nivel. */
function bandBgColor(band: string): string {
  switch (band) {
    case 'optimizado':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'gestionado':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'en_desarrollo':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'inicial':
    default:
      return 'bg-red-100 text-red-800 border-red-200';
  }
}

/** Color del badge de severidad de hallazgo. */
function severityStyle(severity: 'low' | 'medium' | 'high' | 'critical'): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'high':
      return 'bg-orange-100 text-orange-800 border border-orange-200';
    case 'medium':
      return 'bg-amber-100 text-amber-800 border border-amber-200';
    case 'low':
    default:
      return 'bg-blue-100 text-blue-800 border border-blue-200';
  }
}

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critico',
  high: 'Alto',
  medium: 'Medio',
  low: 'Bajo',
};

export function CaptureResultPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [result, setResult] = useState<CaptureResultView | null>(null);
  const [errorKind, setErrorKind] = useState<ResultErrorKind | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchCaptureResult(token)
      .then(setResult)
      .catch((err: unknown) => setErrorKind(classifyResultError(err)));
  }, [token]);

  if (errorKind) return <ResultError kind={errorKind} />;
  if (!result) return <ResultLoading />;
  if (!result.submitted) return <ResultNotSubmitted />;

  return <ResultContent result={result} />;
}

function ResultLoading(): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-sm text-slate-500">Cargando resultados...</p>
    </main>
  );
}

function ResultNotSubmitted(): JSX.Element {
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cumple-primary-700 text-white font-bold mb-4">
          ?
        </div>
        <h1 className="text-xl font-semibold text-slate-900">
          Aun no has completado el formulario
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          Los resultados estaran disponibles una vez que envies tu autoevaluacion. Vuelve a este
          enlace despues de completar el formulario.
        </p>
        <p className="mt-6 text-xs text-slate-400">Cumple · un producto de MSSPRD</p>
      </div>
    </main>
  );
}

function ResultError({ kind }: { kind: ResultErrorKind }): JSX.Element {
  const titles: Record<ResultErrorKind, string> = {
    not_found: 'Enlace invalido',
    expired: 'Enlace expirado',
    revoked: 'Enlace revocado',
    rate_limited: 'Demasiados intentos',
    not_submitted: 'Formulario no enviado',
    unknown: 'No se pudo cargar el resultado',
  };
  const descriptions: Record<ResultErrorKind, string> = {
    not_found:
      'El enlace no es valido. Verifica que copiaste la URL completa o solicita uno nuevo a tu contacto MSSPRD.',
    expired: 'El enlace expiro. Si necesitas acceder a tu resultado, contacta a tu auditor MSSPRD.',
    revoked: 'El enlace fue revocado por MSSPRD. Contacta a tu auditor.',
    rate_limited:
      'Demasiados intentos consecutivos. Espera unos minutos antes de volver a intentar.',
    not_submitted: 'El formulario aun no ha sido enviado. Completa tu autoevaluacion primero.',
    unknown: 'Ocurrio un error inesperado. Intenta mas tarde o contacta a MSSPRD.',
  };
  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-700 text-white font-bold mb-4">
          C
        </div>
        <h1 className="text-xl font-semibold text-slate-900">{titles[kind]}</h1>
        <p className="mt-3 text-sm text-slate-600">{descriptions[kind]}</p>
        <p className="mt-6 text-xs text-slate-400">Cumple · un producto de MSSPRD</p>
      </div>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sprint II · ADR-042 D4 — Botón de descarga del PDF de autoevaluación
// ─────────────────────────────────────────────────────────────────────

type DownloadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'generating' }
  | { kind: 'error'; message: string };

const BACKOFF_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 16_000];

/**
 * Botón "Descargar reporte (PDF)" con lógica de reintento/backoff.
 *
 * - Si el backend responde 202 (generating), reintenta con backoff exponencial
 *   hasta ~5 intentos. El estado "generando" NO bloquea el resto de la página.
 * - Si recibe el blob (200), dispara la descarga directa en el navegador.
 * - Si tras reintentos sigue generando, o llega un 409 report_unavailable,
 *   muestra mensaje de contacto.
 *
 * Solo visible cuando `result.submitted && result.global_score !== null`.
 */
function DownloadReportButton({ token }: { token: string }): JSX.Element {
  const [state, setState] = useState<DownloadState>({ kind: 'idle' });
  const retryRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerDownload = useCallback((blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'autoevaluacion-cumple.pdf';
    a.click();
    URL.revokeObjectURL(url);
    setState({ kind: 'idle' });
  }, []);

  const attempt = useCallback((): void => {
    fetchCaptureReport(token)
      .then((result) => {
        if (result.status === 'ready') {
          triggerDownload(result.blob);
          return;
        }
        // 202 generating — reintentar con backoff.
        const delay =
          BACKOFF_DELAYS_MS[retryRef.current] ?? BACKOFF_DELAYS_MS[BACKOFF_DELAYS_MS.length - 1];
        retryRef.current += 1;
        if (retryRef.current > BACKOFF_DELAYS_MS.length) {
          // Agotamos los reintentos.
          setState({
            kind: 'error',
            message:
              'Tu reporte todavía está generándose. Vuelve a intentar en unos minutos o contacta a tu auditor MSSPRD.',
          });
          return;
        }
        // Aviso visual de que está generando.
        setState({ kind: 'generating' });
        timeoutRef.current = setTimeout(attempt, delay);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('409:report_unavailable')) {
          setState({
            kind: 'error',
            message:
              'El reporte no está disponible para esta autoevaluación. Contacta a tu auditor MSSPRD.',
          });
        } else {
          setState({
            kind: 'error',
            message: 'No fue posible descargar el reporte. Intenta más tarde o contacta a MSSPRD.',
          });
        }
      });
  }, [token, triggerDownload]);

  const handleClick = useCallback(() => {
    if (state.kind === 'loading' || state.kind === 'generating') return;
    retryRef.current = 0;
    setState({ kind: 'loading' });
    attempt();
  }, [state.kind, attempt]);

  // Limpieza: cancelar timeout si el componente se desmonta.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (state.kind === 'error') {
    return (
      <div
        role="alert"
        className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
      >
        <p>{state.message}</p>
        <button
          type="button"
          onClick={() => setState({ kind: 'idle' })}
          className="mt-2 text-xs underline text-amber-700 hover:text-amber-900"
        >
          Volver a intentar
        </button>
      </div>
    );
  }

  const isWorking = state.kind === 'loading' || state.kind === 'generating';

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={isWorking}
        className="inline-flex items-center gap-2 rounded-xl bg-cumple-primary-700 text-white px-5 py-2.5 text-sm font-semibold hover:bg-cumple-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        aria-busy={isWorking}
      >
        {isWorking ? (
          <>
            <span
              className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"
              aria-hidden="true"
            />
            {state.kind === 'generating' ? 'Generando tu reporte…' : 'Descargando…'}
          </>
        ) : (
          'Descargar reporte (PDF)'
        )}
      </button>
      {state.kind === 'generating' && (
        <p className="text-xs text-slate-500">
          El reporte se está preparando, se descargará automáticamente.
        </p>
      )}
    </div>
  );
}

function ResultContent({ result }: { result: CaptureResultView }): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const globalScore = result.global_score;

  // Normaliza per_domain a la estructura que espera DomainHeatMap.
  const domainEntries = Object.entries(result.per_domain).map(([domain, score]) => ({
    label: domain,
    score: Number(score),
  }));

  return (
    <main className="min-h-screen bg-slate-50">
      <header className="bg-brand-700 text-white px-6 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 font-bold">
              AF
            </span>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70">
                Tu autoevaluacion · MSSPRD
              </p>
              <h1 className="text-xl font-semibold">
                {result.site.name} / {result.site.client_name}
              </h1>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/85">{result.template.name}</p>
          {result.submitted_at && (
            <p className="text-xs text-white/70">
              Completado el{' '}
              {new Date(result.submitted_at).toLocaleDateString('es-DO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        {/* Aviso de resultado preliminar — visible solo en Fase A */}
        {result.informative_only && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800"
          >
            <span className="font-semibold">Resultado preliminar / informativo.</span> Esta
            autoevaluacion es un punto de partida contextual. El equipo MSSPRD realizara una
            evaluacion detallada con reglas de cumplimiento durante la auditoria formal.
          </div>
        )}

        {/* Nivel de cumplimiento (banda de madurez) */}
        {result.level && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-cumple-gray-500 mb-3">
              Nivel de madurez
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span
                className={`inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-semibold ${bandBgColor(result.level.band)}`}
              >
                {result.level.label}
              </span>
              <p className="flex-1 text-sm text-slate-700 min-w-0">{result.level.description}</p>
            </div>
          </div>
        )}

        {/* Botón de descarga del reporte PDF — Sprint II · ADR-042 D4.
            Reubicado aquí (D1 · ADR-044): justo después del nivel de madurez, en la zona
            de mayor atención. El guard se mantiene intacto: solo visible cuando
            global_score !== null (el worker encola el PDF únicamente cuando snapshotScore !== null;
            mostrarlo con score===null produce un ciclo 202→contacto sin salida). */}
        {token && result.global_score !== null && (
          <div className="rounded-2xl bg-cumple-primary-50/40 border border-cumple-primary-100 p-6 space-y-2">
            <h2 className="font-semibold text-slate-900">Descarga tu informe</h2>
            <p className="text-sm text-slate-600">
              Descarga un resumen en PDF de tu autoevaluación con el nivel de madurez, score y
              hallazgos identificados.
            </p>
            <DownloadReportButton token={token} />
          </div>
        )}

        {/* Score global — se omite si no hay snapshot (scoring no disponible) */}
        {globalScore === null ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <p className="font-semibold text-slate-900">Score global</p>
            <p className="text-sm text-slate-600">
              No disponible para esta autoevaluación. El equipo MSSPRD la revisará igualmente.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 flex items-center gap-6">
            <div
              className="text-5xl font-bold shrink-0"
              style={{ color: scoreColor(globalScore) }}
              aria-label={`Score global: ${String(Math.round(globalScore))} de 100`}
            >
              {Math.round(globalScore)}
              <span className="text-base text-slate-500">/100</span>
            </div>
            <div>
              <p className="font-semibold text-slate-900">Score global</p>
              <p className="text-sm text-slate-600">
                {result.informative_only
                  ? 'Valor informativo basado en el contexto completado.'
                  : 'Promedio ponderado de los dominios evaluados.'}
              </p>
            </div>
          </div>
        )}

        {/* Mapa de calor por dominio */}
        {domainEntries.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-3">
            <h2 className="font-semibold text-slate-900">Detalle por dominio</h2>
            <DomainHeatMap domains={domainEntries} />
          </div>
        )}

        {/* Hallazgos automaticos */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-3">
          <h2 className="font-semibold text-slate-900">Hallazgos identificados</h2>
          {result.findings.length === 0 ? (
            <p className="text-sm text-slate-600">
              Sin hallazgos automaticos en esta autoevaluacion (preliminar). El auditor realizara
              una revision detallada durante la visita formal.
            </p>
          ) : (
            <ul className="space-y-2">
              {result.findings.map((f, idx) => (
                <li
                  // Clave estable en datos inmutables: indice + texto truncado
                  key={`${String(idx)}-${f.question_text.slice(0, 20)}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50"
                >
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold shrink-0 ${severityStyle(f.severity)}`}
                  >
                    {SEVERITY_LABEL[f.severity] ?? f.severity}
                  </span>
                  <span className="text-sm text-slate-800">{f.question_text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Mensaje de cierre orientado a valor */}
        <div className="rounded-2xl bg-brand-700 text-white p-6">
          <h2 className="font-semibold text-lg">Proximos pasos</h2>
          <p className="text-sm text-white/85 mt-2">
            Este resultado es tu punto de partida. El equipo MSSPRD analizara tus respuestas,
            profundizara en las brechas identificadas y te acompanara en la construccion de un plan
            de mejora concreto y priorizado.
          </p>
          <p className="text-sm text-white/70 mt-3">
            Pronto recibiras noticias de tu auditor con los siguientes pasos del proceso.
          </p>
        </div>

        <footer className="text-center text-xs text-slate-400 pt-4">
          Cumple · un producto de MSSPRD · Resultado confidencial del cliente
        </footer>
      </section>
    </main>
  );
}
